import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import AttachmentService from "services/Attachment";
import SettingsService from "services/Settings";
import PageTitleBar from "components/PageTitleBar";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { SaveButton } from "components/ui/save-button";
import {
  globalSettingsSchema,
  toFormValues,
  toSettingsPayload,
} from "routes/settings/global/globalSettingsForm";
import { CallCenterCard } from "routes/settings/global/CallCenterCard";
import { GlobalSettingsSkeleton } from "routes/settings/global/GlobalSettingsSkeleton";
import { PiracyCard } from "routes/settings/global/PiracyCard";

const GLOBAL_SETTINGS_QUERY_KEY = ["settings", "global"];

// Enter in a text field would otherwise submit the whole page mid-edit.
const preventImplicitSubmit = event => {
  if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
    event.preventDefault();
  }
};

const uploadPendingVoicemail = async values => {
  const voicemail = values.call_center.system_voicemail;
  if (!(voicemail instanceof File)) return values;
  const attachment = await AttachmentService.uploadFile(voicemail);
  return {
    ...values,
    call_center: { ...values.call_center, system_voicemail: attachment },
  };
};

function GlobalSettings() {
  const queryClient = useQueryClient();
  const {
    data: settings,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: GLOBAL_SETTINGS_QUERY_KEY,
    queryFn: () => SettingsService.getSettings(true),
  });
  const { mutateAsync: updateSettings } = useMutation({
    mutationFn: newSettings => SettingsService.updateSettings(newSettings),
    // Invalidates the whole "settings" prefix so the map distance bands pick
    // up new limits along with this page.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings"] }),
  });

  const initialValues = useMemo(() => toFormValues(settings), [settings]);

  const form = useForm({
    resolver: zodResolver(globalSettingsSchema),
    values: initialValues,
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = form;

  const onSubmit = async values => {
    try {
      const uploaded = await uploadPendingVoicemail(values);
      await updateSettings(toSettingsPayload(uploaded, initialValues));
      toast.success("Settings updated");
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center">
      <div className="tw:flex tw:flex-col tw:gap-4 tw:w-full tw:max-w-3xl">
        <PageTitleBar title="Global Settings" />
        {isLoading && <GlobalSettingsSkeleton />}
        {loadError && (
          <Alert variant="destructive">
            <AlertTitle>Settings could not be loaded</AlertTitle>
            <AlertDescription>{loadError.message}</AlertDescription>
          </Alert>
        )}
        {settings && (
          <FormProvider {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={preventImplicitSubmit}
              className="tw:flex tw:flex-col tw:gap-4"
              noValidate
            >
              <CallCenterCard />
              <PiracyCard />
              <div className="tw:flex tw:justify-end">
                <SaveButton
                  type="submit"
                  saving={isSubmitting}
                  disabled={!isDirty}
                />
              </div>
            </form>
          </FormProvider>
        )}
      </div>
    </div>
  );
}

export default GlobalSettings;
